<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { computed } from 'vue';

import HorizontalProductCard from '@/components/base/layout/HorizontalProductCard.vue';
import Slider from '@/components/base/Slider.vue';
import TagBadge from '@/components/base/TagBadge.vue';
import Caption from '@/components/base/typography/Caption.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import money from '@/filters/money';
import { AnalyticsMetadata, BaseChoice } from '@/lib/personalization/dynamicYield';
import {
  formatAnalyticsMetadata,
  ListSourceAttribution,
} from '@/utils/analytics/listSourceAttribution';
import { ProductClickedPageSection } from '@/utils/analytics/productClickedEvent';
import { ProductListViewedEventData } from '@/utils/analytics/productListViewedEvent';
import { isDefined } from '@/utils/isDefined';
import { displayPrice, ProductCardData } from '@/utils/productCard';

const props = withDefaults(
  defineProps<{
    analyticsList?: string;
    analyticsMetadata?: AnalyticsMetadata;
    decisionId?: BaseChoice['decisionId'];
    link?: string;
    pageSection?: ProductClickedPageSection | ProductListViewedEventData['pageSection'];
    recommendations: ProductCardData[];
    title?: string;
    trackingEventLocation?: string;
  }>(),
  {
    title: 'You May Also like',
  },
);

const breakpoints = useBreakpoints(breakpointsTailwind);
const breakpointBelowSm = breakpoints.smaller('sm');
const breakpointBelowMd = breakpoints.smaller('md');
const scrollWidth = computed(() => {
  if (breakpointBelowSm.value) {
    return 344;
  }
  if (breakpointBelowMd.value) {
    return 306;
  }
  return undefined;
});

const { flags } = useFeatureFlags();

const listSourceAttribution = computed<ListSourceAttribution | undefined>(
  () =>
    props.analyticsMetadata && {
      ...formatAnalyticsMetadata(props.analyticsMetadata),
      listSource: 'Dynamic Yield',
      listTitle: props.title,
    },
);
</script>

<template>
  <div v-if="recommendations.length" class="flex items-end" data-test="slim-recommendations">
    <Slider justifyContent="start" :link :list="recommendations" :scrollWidth :title>
      <HorizontalProductCard
        v-for="(recommendation, index) in recommendations"
        class="w-[344px] sm:w-[306px] md:w-[368px] lg:w-[330px] px-4 border-r border-solid border-neutral-200 last:border-0 snap-start snap-always md:snap-normal"
        :analyticsList
        :key="recommendation.sku"
        :decisionId
        imageSize="medium"
        impressionTrackable
        :index
        :listSourceAttribution
        :pageSection
        :product="recommendation"
        textSize="small"
        :trackingEventLocation
      >
        <template v-slot:badge>
          <TagBadge
            v-if="!isDefined(flags.socialProofMessaging)"
            class="hidden absolute top-0 left-4"
            alignment="left"
            :tags="recommendation.totalSavings ? ['badge-onsale'] : recommendation.keywords ?? []"
          />
        </template>
        <template v-slot:price>
          <span v-if="recommendation.totalSavings" class="sr-only">Price reduced from </span>
          <SmallBodyText
            :class="recommendation.totalSavings?.onSale ? 'text-nuts-red-800' : 'text-neutral-500'"
          >
            {{ recommendation.hidePrice ? 'Build your own' : displayPrice(recommendation) }}
          </SmallBodyText>
          <span v-if="recommendation.totalSavings" class="sr-only"> to </span>
          <Caption v-if="recommendation.totalSavings" class="ml-1.5 line-through text-neutral-500">
            {{ money(recommendation.totalSavings.comparisonPrice) }}
          </Caption>
        </template>
      </HorizontalProductCard>
    </Slider>
  </div>
</template>
